import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ConversationEventMessage } from '../connectivity';
import { Conversation, ConversationStatus, ConversationMetada } from '../models/chat';
import { GeneralCampaign } from '../models/campaign';

@Injectable({
  providedIn: 'root'
})
export class ConversationCommunicationService {

  private conversationEventSubject = new Subject<ConversationEventMessage>();
  public conversationEvent$: Observable<ConversationEventMessage> = this.conversationEventSubject.asObservable();
  // Optional per-session filter: if set, only emit events for these campaign ids
  private allowedCampaignIds: Set<string> | null = null;

  constructor() { }

  get conversationEvents(): Observable<ConversationEventMessage> {
    return this.conversationEvent$;
  }

  /**
   * Emit a conversation event message to subscribers
   * @param message The conversation event message to emit
   */
  emitConversationEvent(conversation: ConversationEventMessage): void {
    // If a campaign filter is configured, drop events that don't belong to allowed campaigns
    try {
      const campaignId = conversation?.campaign_id ?? conversation?.campaignId ?? null;
      if (this.allowedCampaignIds !== null) {
        if (!campaignId || !this.allowedCampaignIds.has(String(campaignId))) {
          // silently ignore event not allowed for current session
          return;
        }
      }
    } catch (e) {
      // In case of any unexpected error, fall back to emitting the event
      console.warn('ConversationCommunicationService: error applying allowedCampaignIds filter', e);
    }

    this.conversationEventSubject.next(conversation);
  }

  /**
   * Configure allowed campaign ids for this client session.
   * Pass `null` to disable filtering.
   */
  setAllowedCampaigns(ids: string[] | null): void {
    // null means no filtering; [] means deny-all (fail-closed).
    if (ids === null || ids === undefined || !Array.isArray(ids)) {
      this.allowedCampaignIds = null;
    } else {
      this.allowedCampaignIds = new Set(ids.map(String));
    }
  }

  /**
   * Convenience: return an observable filtered by the provided campaign ids without changing global service filter
   */
  getConversationEventsForCampaigns(ids: string[] | null) : Observable<ConversationEventMessage> {
    if (!ids || ids.length === 0) return this.conversationEvent$;
    const allowed = new Set(ids.map(String));
    return this.conversationEvent$.pipe(
      // filter at observable level
      // Note: import 'filter' from rxjs/operators where used by consumers
      // Consumers should subscribe to this observable when they want filtered events
      // We keep implementation minimal here
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (source: Observable<ConversationEventMessage>) => new Observable<ConversationEventMessage>(subscriber => {
        const sub = (source as any).subscribe({
          next: (ev: ConversationEventMessage) => {
            const campaignId = ev?.campaign_id ?? ev?.campaignId;
            if (!campaignId) return;
            if (allowed.has(String(campaignId))) subscriber.next(ev);
          },
          error: (err: unknown) => subscriber.error(err),
          complete: () => subscriber.complete()
        });
        return () => sub.unsubscribe();
      })
    );
  }

 convertConversationEventMessageToConversation(
    event: ConversationEventMessage
  ): Conversation {
  // Helper to safely parse dates
  const parseDate = (dateStr?: string | null): Date => {
    return dateStr ? new Date(dateStr) : new Date();
  };

  const keyPrefix = event.key_prefix ?? event.keyPrefix;
  const startDate = event.start_date ?? event.startDate;
  const clientId = event.client_id ?? event.clientId ?? undefined;
  const channelId = event.channel_id ?? event.channelId ?? undefined;
  const workspaceId = event.workspace_id ?? event.workspaceId ?? undefined;
  const agentId = event.agent_id ?? event.agentId ?? undefined;
  const agentName = event.agent_name ?? event.agentName ?? undefined;
  const endDate = event.end_date ?? event.endDate;
  const acceptedDate = event.accepted_date ?? event.acceptedDate;
  const closureReason = event.closure_reason ?? event.closureReason ?? undefined;
  const campaignId = event.campaign_id ?? event.campaignId ?? '';
  const ticketId = event.ticket_id ?? event.ticketId ?? undefined;
  const targetScope = event.target_scope ?? event.targetScope ?? undefined;
  const metadata = event.metadata;
  const customVariables = event.custom_variables ?? event.customVariables;

  // Map string status to ConversationStatus enum
  const mapStatus = (status?: string): ConversationStatus => {
    switch (String(status || '')) {
      case ConversationStatus.Active:
      case 'active':
        return ConversationStatus.Active;
      case ConversationStatus.Finish:
      case 'finish':
      case 'closed':
        return ConversationStatus.Finish;
      case ConversationStatus.Pending:
      case 'pending':
      default:
        return ConversationStatus.Pending;
    }
  };

  return {
    conversationId: event.conversation_id ?? event.conversationId,
    keyPrefix,
    startDate: parseDate(startDate),
    clientId,
    channelId,
    platform: event.platform ?? undefined,
    workspaceId,
    status: mapStatus(event.status ?? undefined),
    endDate: parseDate(endDate),
    acceptedDate: acceptedDate ? new Date(acceptedDate) : undefined,
    agentId,
    agentName,
    campaignId,
    targetScope,
    ticketId,
    metadata: metadata
      ? {
        getgeneralConfig: metadata.general_config
          ? {
            accept_conversation: metadata.general_config.accept_conversation,
            enable_file_upload: metadata.general_config.enable_file_upload,
            enable_file_download: metadata.general_config.enable_file_download,
          } as GeneralCampaign // Casting here since `GeneralCampaign` shape isn't fully given
          : metadata.generalConfig
            ? {
              accept_conversation: !!metadata.generalConfig.acceptConversation,
              enable_file_upload: !!metadata.generalConfig.enableFileUpload,
              enable_file_download: !!metadata.generalConfig.enableFileDownload,
            } as GeneralCampaign
            : undefined,
        translationConfig: metadata.translation_config
          ? {
            enable: metadata.translation_config.enable,
            sourceLanguage: metadata.translation_config.source_language ?? undefined,
            targetLanguage: metadata.translation_config.target_language ?? undefined,
          }
          : metadata.translationConfig
            ? {
              enable: !!metadata.translationConfig.enable,
              sourceLanguage: metadata.translationConfig.sourceLanguage ?? undefined,
              targetLanguage: metadata.translationConfig.targetLanguage ?? undefined,
            }
          : undefined,
      }
      : undefined,
    customVariables,
  };
}

}
